import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, Monitor, Wrench, Globe, Settings, Smartphone, ChevronDown, User, LogOut, Ticket, CreditCard, FileText, Crown, Users, Database, Grid } from 'lucide-react';
import { FaAndroid } from 'react-icons/fa';
import { Link } from './ui/Link';
import { Modal } from './ui/Modal';
import LoginForm from './LoginForm';
import RegisterModal from './RegisterModal';
import { useAuth } from '../contexts/AuthContext';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { getImageUrl, DEFAULT_IMAGE } from '../utils/imageUtils';

// Types pour la configuration des onglets
type TabId = 'profile' | 'dashboard' | 'freelancers' | 'users' | 'tickets' | 'subscription' | 'invoices' | 'preferences' | 'ticket_database';

type TabConfig = {
  id: TabId;
  label: string;
  icon: React.ReactNode;
  roles?: string[];
  excludeRoles?: string[];
};

// Configuration des onglets du dashboard
const tabConfigs: TabConfig[] = [
  {
    id: 'profile',
    label: 'Mon profil',
    icon: <User className="w-5 h-5" />,
  },
  {
    id: 'dashboard',
    label: 'Tableau de bord',
    icon: <Grid className="w-5 h-5" />,
    roles: ['fondateur', 'admin', 'freelancer_admin'],
  },
  {
    id: 'users',
    label: 'Les utilisateurs',
    icon: <Users className="w-5 h-5" />,
    roles: ['fondateur', 'admin', 'freelancer_admin'],
  },
  {
    id: 'freelancers',
    label: 'Les freelancers',
    icon: <Users className="w-5 h-5" />,
    roles: ['fondateur', 'admin', 'freelancer_admin'],
  },
  {
    id: 'tickets',
    label: 'Mes tickets',
    icon: <Ticket className="w-5 h-5" />,
    excludeRoles: ['admin', 'fondateur', 'freelancer', 'freelancer_admin'],
  },
  {
    id: 'ticket_database',
    label: 'Base de tickets',
    icon: <Database className="w-5 h-5" />,
    roles: ['fondateur', 'admin', 'freelancer', 'freelancer_admin'],
  },
  {
    id: 'subscription',
    label: 'Mon abonnement',
    icon: <CreditCard className="w-5 h-5" />,
    excludeRoles: ['admin', 'fondateur', 'freelancer', 'freelancer_admin'],
  },
  {
    id: 'invoices',
    label: 'Mes factures',
    icon: <FileText className="w-5 h-5" />,
    excludeRoles: ['admin', 'fondateur', 'freelancer', 'freelancer_admin'],
  },
  {
    id: 'preferences',
    label: 'Préférences',
    icon: <Settings className="w-5 h-5" />,
  }
];

// Fonction helper pour vérifier si un utilisateur a accès à un onglet
const canAccessTab = (tab: TabConfig, userRole?: string): boolean => {
  if (!userRole) return !tab.roles && !tab.excludeRoles;
  
  if (tab.roles && tab.roles.length > 0) {
    return tab.roles.includes(userRole);
  }
  
  if (tab.excludeRoles && tab.excludeRoles.length > 0) {
    return !tab.excludeRoles.includes(userRole);
  }
  
  return true;
};

type SubscriptionType = "none" | "solo" | "family";

type NavDropdownItem = { name: string; href: string; icon: React.ReactNode };

type NavService = { name: string; href: string; icon: React.ReactNode; dropdown: NavDropdownItem[] };

const Navbar: React.FC = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileDropdowns, setMobileDropdowns] = useState<Record<number, boolean>>({});
  /** Sous-menu desktop ouvert (index du service), piloté en état pour pouvoir le fermer au clic. */
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const { user, isAuthenticated, logout } = useAuth();
  const userMenuRef = useRef<HTMLDivElement>(null);
  const subscriptionType: SubscriptionType =
    user && user.subscriptionType ? (user.subscriptionType as SubscriptionType) : "none";
  // URL de la photo en erreur : l'erreur se réinitialise d'elle-même quand la photo change
  const [failedProfileImage, setFailedProfileImage] = useState<string | null>(null);
  const profileImageError = failedProfileImage !== null && failedProfileImage === user?.profileImage;
  const routeKey = `${location.pathname}${location.hash}`;
  const [prevRouteKey, setPrevRouteKey] = useState(routeKey);

  // Même hauteur réservée (pt-header / md:pt-header-md) + état scroll cohérent au changement de page
  if (prevRouteKey !== routeKey) {
    setPrevRouteKey(routeKey);
    setIsScrolled(window.scrollY > 10);
    setIsOpen(false);
    setMobileDropdowns({});
    setOpenDropdown(null);
  }
  


  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuRef]);

  const services: NavService[] = [
    {
      name: "Réparation téléphone",
      href: "/reparation-telephone",
      icon: <Smartphone className="h-5 w-5" />,
      dropdown: [
        {
          name: "iPhone",
          href: "/reparation-telephone#devis",
          icon: <Smartphone className="h-5 w-5" />
        },
        {
          name: "Android",
          href: "/reparation-telephone#devis",
          icon: <FaAndroid className="h-5 w-5" />
        }
      ]
    },
    {
      name: "Dépannage informatique",
      href: "/depannage-informatique",
      icon: <Wrench className="h-5 w-5" />,
      dropdown: [
        {
          name: "Particuliers",
          href: "/depannage-informatique#particuliers",
          icon: <User className="h-5 w-5" />
        },
        {
          name: "TPE/PME",
          href: "/depannage-informatique#tpe-pme",
          icon: <Users className="h-5 w-5" />
        }
      ]
    },
    {
      name: "Création site web",
      href: "/creation-site-web",
      icon: <Globe className="h-5 w-5" />,
      dropdown: [
        {
          name: "Offre site pro",
          href: "/creation-site-web#web-vitrine",
          icon: <Monitor className="h-5 w-5" />
        },
        {
          name: "E-commerce sur devis",
          href: "/creation-site-web#web-ecommerce",
          icon: <CreditCard className="h-5 w-5" />
        }
      ]
    }
  ];

  const handleLoginSuccess = () => {
    setIsLoginModalOpen(false);
  };

  const handleUserButtonClick = () => {
    if (isAuthenticated) {
      setIsUserMenuOpen(!isUserMenuOpen);
    } else {
      setIsLoginModalOpen(true);
    }
  };

  const openRegisterModal = () => {
    setIsLoginModalOpen(false);
    setIsRegisterModalOpen(true);
  };

  const openLoginModal = () => {
    setIsRegisterModalOpen(false);
    setIsLoginModalOpen(true);
  };

  const getSubscriptionName = () => {
    switch(subscriptionType) {
      case "solo": return "Plan Solo";
      case "family": return "Plan Famille";
      case "none": 
      default: return "A la carte";
    }
  };

  /** Ferme le sous-menu desktop et retire le focus du lien cliqué (sinon il resterait « actif »). */
  const closeDropdown = () => {
    setOpenDropdown(null);
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
  };

  const handleDropdownBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpenDropdown(null);
  };

  const toggleMobileDropdown = (index: number) => {
    setMobileDropdowns(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  return (
    <>
      <nav 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || isOpen ? 'bg-canvas/85 backdrop-blur-md border-b border-line' : 'bg-transparent border-b border-transparent'
        }`}
      >
        <div className="container-site">
          <div className="flex justify-between items-center h-header md:h-header-md">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <RouterLink to="/" className="flex items-center">
                  <img
                    src="/images/brand/logo-image.webp" width={171} height={256} fetchPriority="high"
                    alt="SOS IT Services"
                    className="h-14 md:h-[68px] w-auto object-contain bg-transparent"
                  />
                </RouterLink>
              </div>
            </div>
            
            <div className="hidden md:block">
              <div className="ml-10 flex items-center gap-1 lg:gap-2">
                {services.map((service, index) => (
                  <div
                    key={index}
                    className="relative"
                    onMouseEnter={() => setOpenDropdown(index)}
                    onMouseLeave={() => setOpenDropdown(null)}
                    onBlur={handleDropdownBlur}
                    onKeyDown={(e) => { if (e.key === 'Escape') closeDropdown(); }}
                  >
                    <div>
                      <button
                        type="button"
                        aria-haspopup="true"
                        aria-expanded={openDropdown === index}
                        onClick={() => setOpenDropdown(openDropdown === index ? null : index)}
                        className={`flex items-center hover:text-fg px-3 py-2 rounded-full text-sm font-medium transition-colors [&>svg:first-child]:hidden lg:[&>svg:first-child]:block ${openDropdown === index ? 'text-fg' : 'text-fg-muted'}`}
                      >
                        {service.icon}
                        <span className="ml-2">{service.name}</span>
                        <ChevronDown className={`ml-1 h-4 w-4 text-fg-subtle transition-transform duration-200 ${openDropdown === index ? 'rotate-180' : ''}`} />
                      </button>
                      <div
                        className={`absolute left-0 pt-2 w-56 transition-all duration-200 origin-top-left ${openDropdown === index ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
                        onClick={closeDropdown}
                      >
                        <div className="p-1.5 rounded-xl border border-line bg-surface shadow-[0_12px_32px_-12px_rgb(0_0_0/0.6)]">
                          {service.dropdown.map((item, dropdownIndex) => (
                            <Link
                              key={dropdownIndex}
                              href={item.href}
                              className="flex items-center px-3 py-2.5 rounded-lg text-sm text-fg-muted hover:text-fg hover:bg-surface-2 transition-colors [&>svg]:text-accent-soft"
                            >
                              {item.icon}
                              <span className="ml-2">{item.name}</span>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <RouterLink
                  to="/contact"
                  onClick={closeDropdown}
                  className="btn-primary !px-5 !py-2.5 !text-sm ml-2"
                >
                  Me contacter
                </RouterLink>
                <div className="relative" ref={userMenuRef}>
                  <button 
                    className="flex items-center justify-center h-11 w-11 rounded-full border border-line hover:border-line-strong transition-colors"
                    aria-label={isAuthenticated ? 'Menu utilisateur' : 'Se connecter'}
                    onClick={handleUserButtonClick}
                  >
                    {isAuthenticated ? (
                      (() => {
                        const avatarUrl = user?.profileImage ? getImageUrl(user.profileImage) : null;
                        const hasRealPhoto = avatarUrl && avatarUrl !== DEFAULT_IMAGE && !profileImageError;
                        return hasRealPhoto ? (
                          <img
                            src={avatarUrl}
                            alt={`${user!.firstName} ${user!.lastName}`}
                            className="h-8 w-8 rounded-full object-cover"
                            onError={() => setFailedProfileImage(user?.profileImage ?? null)}
                            key={user!.profileImage}
                          />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-accent-strong flex items-center justify-center">
                            <User className="h-5 w-5 text-fg" />
                          </div>
                        );
                      })()
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-surface flex items-center justify-center">
                        <User className="h-4 w-4 text-fg-muted" />
                      </div>
                    )}
                  </button>
                  
                  {/* Menu utilisateur */}
                  {isAuthenticated && (
                    <div 
                      className={`absolute right-0 mt-2 w-48 rounded-xl border border-line shadow-[0_12px_32px_-12px_rgb(0_0_0/0.6)] bg-surface transition-all duration-200 ${isUserMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
                      style={{ minWidth: 'max-content', maxWidth: '90vw', overflowX: 'auto' }}
                    >
                      <div className="py-1">
                        <div className="px-4 py-2 text-sm text-fg-muted border-b border-line">
                          Connecté en tant que<br />
                          <div className="flex flex-col mt-1">
                            <span className="font-semibold text-fg mb-1">{user?.firstName} {user?.lastName}</span>
                            <span className="text-xs text-fg-muted">{user?.email}</span>
                          </div>
                        </div>
                        
                        {/* Générer les liens de navigation dynamiquement */}
                        {tabConfigs
                          .filter(tab => canAccessTab(tab, user?.role))
                          .map(tab => (
                            <RouterLink
                              key={tab.id}
                              to={`/mon-espace?tab=${tab.id}`}
                              className="flex items-center px-4 py-2 text-sm text-fg-muted hover:text-fg hover:bg-surface-2 transition-colors"
                              onClick={() => setIsUserMenuOpen(false)}
                            >
                              {tab.icon}
                              <span className="ml-2">{tab.label}</span>
                            </RouterLink>
                          ))}
                        
                        <button
                          className="flex items-center px-4 py-2 text-sm text-fg-muted hover:text-red-400 hover:bg-red-500/10 transition-colors w-full text-left"
                          onClick={() => {
                            logout();
                            setIsUserMenuOpen(false);
                          }}
                        >
                          <LogOut className="w-5 h-5 mr-2" />
                          <span>Déconnexion</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="md:hidden">
              <button
                onClick={() => setIsOpen(!isOpen)}
                aria-expanded={isOpen}
                className="inline-flex items-center justify-center h-11 w-11 -mr-2 rounded-full text-fg-muted hover:text-fg"
              >
                {isOpen ? (
                  <X className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <Menu className="block h-6 w-6" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <div className={`md:hidden ${isOpen ? 'block' : 'hidden'}`}>
          <div className="px-4 pt-2 pb-4 space-y-1 sm:px-6 bg-canvas border-t border-line max-h-[calc(100dvh-var(--spacing-header))] overflow-y-auto">
            {services.map((service, index) => (
              <div key={index}>
                <button
                  type="button"
                  onClick={() => toggleMobileDropdown(index)}
                  aria-expanded={!!mobileDropdowns[index]}
                  className="flex items-center w-full text-fg hover:text-fg px-3 py-3 rounded-lg text-base font-medium [&>svg:first-child]:text-accent-soft"
                >
                  {service.icon}
                  <span className="ml-2">{service.name}</span>
                  <ChevronDown className={`ml-1 h-4 w-4 transition-transform duration-200 ${mobileDropdowns[index] ? 'rotate-180' : ''}`} />
                </button>
                <div className={`pl-6 space-y-1 transition-all duration-200 ${mobileDropdowns[index] ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
                  {service.dropdown.map((item, dropdownIndex) => (
                    <RouterLink
                      key={dropdownIndex}
                      to={item.href}
                      className="flex items-center min-h-11 text-fg-muted hover:text-fg px-3 py-2 rounded-lg text-base font-medium"
                      onClick={() => setIsOpen(false)}
                    >
                      {item.icon}
                      <span className="ml-2">{item.name}</span>
                    </RouterLink>
                  ))}
                </div>
              </div>
            ))}
            <RouterLink
              to="/contact"
              onClick={() => setIsOpen(false)}
              className="btn-primary w-full !justify-start !px-4 mt-2"
            >
              Me contacter
            </RouterLink>
            {/* Ajout du lien Mon espace pour mobile */}
            {isAuthenticated && (
              <>
                <RouterLink 
                  to="/mon-espace"
                  className="bg-surface text-fg-muted hover:text-fg flex items-center min-h-11 px-3 py-2 rounded-lg text-base font-medium w-full [&>svg]:text-accent-soft"
                  onClick={() => setIsOpen(false)}
                >
                  <User className="h-5 w-5 mr-2" />
                  <span>Mon profil</span>
                </RouterLink>
                {(user?.role === 'fondateur' || user?.role === 'admin' || user?.role === 'freelancer_admin') && (
                  <>
                    <RouterLink
                      to="/mon-espace?tab=dashboard"
                      className="bg-surface text-fg-muted hover:text-fg flex items-center min-h-11 px-3 py-2 rounded-lg text-base font-medium w-full [&>svg]:text-accent-soft"
                      onClick={() => setIsOpen(false)}
                    >
                      <Grid className="h-5 w-5 mr-2" />
                      <span>Tableau de bord</span>
                    </RouterLink>
                    <RouterLink
                      to="/mon-espace?tab=users"
                      className="bg-surface text-fg-muted hover:text-fg flex items-center min-h-11 px-3 py-2 rounded-lg text-base font-medium w-full [&>svg]:text-accent-soft"
                      onClick={() => setIsOpen(false)}
                    >
                      <Users className="h-5 w-5 mr-2" />
                      <span>Les utilisateurs</span>
                    </RouterLink>
                    <RouterLink
                      to="/mon-espace?tab=freelancers"
                      className="bg-surface text-fg-muted hover:text-fg flex items-center min-h-11 px-3 py-2 rounded-lg text-base font-medium w-full [&>svg]:text-accent-soft"
                      onClick={() => setIsOpen(false)}
                    >
                      <Users className="h-5 w-5 mr-2" />
                      <span>Les freelancers</span>
                    </RouterLink>
                  </>
                )}
                {(user?.role === 'fondateur' || user?.role === 'admin' || user?.role === 'freelancer' || user?.role === 'freelancer_admin') && (
                  <RouterLink
                    to="/mon-espace?tab=ticket_database"
                    className="bg-surface text-fg-muted hover:text-fg flex items-center min-h-11 px-3 py-2 rounded-lg text-base font-medium w-full [&>svg]:text-accent-soft"
                    onClick={() => setIsOpen(false)}
                  >
                    <Database className="h-5 w-5 mr-2" />
                    <span>Base de tickets</span>
                  </RouterLink>
                )}
                {(!user?.role || 
                  (user?.role !== 'fondateur' && 
                   user?.role !== 'freelancer' &&
                   user?.role !== 'freelancer_admin')) && (
                  <>
                    <RouterLink 
                      to="/mon-espace?tab=tickets"
                      className="bg-surface text-fg-muted hover:text-fg flex items-center min-h-11 px-3 py-2 rounded-lg text-base font-medium w-full [&>svg]:text-accent-soft"
                      onClick={() => setIsOpen(false)}
                    >
                      <Ticket className="h-5 w-5 mr-2" />
                      <span>Mes tickets</span>
                    </RouterLink>
                    <RouterLink 
                      to="/mon-espace?tab=subscription"
                      className="bg-surface text-fg-muted hover:text-fg flex items-center min-h-11 px-3 py-2 rounded-lg text-base font-medium w-full [&>svg]:text-accent-soft"
                      onClick={() => setIsOpen(false)}
                    >
                      <CreditCard className="h-5 w-5 mr-2" />
                      <span>Mon abonnement</span>
                    </RouterLink>
                    <RouterLink 
                      to="/mon-espace?tab=invoices"
                      className="bg-surface text-fg-muted hover:text-fg flex items-center min-h-11 px-3 py-2 rounded-lg text-base font-medium w-full [&>svg]:text-accent-soft"
                      onClick={() => setIsOpen(false)}
                    >
                      <FileText className="h-5 w-5 mr-2" />
                      <span>Mes factures</span>
                    </RouterLink>
                  </>
                )}
                <RouterLink 
                  to="/mon-espace?tab=preferences"
                  className="bg-surface text-fg-muted hover:text-fg flex items-center min-h-11 px-3 py-2 rounded-lg text-base font-medium w-full [&>svg]:text-accent-soft"
                  onClick={() => setIsOpen(false)}
                >
                  <Settings className="h-5 w-5 mr-2" />
                  <span>Préférences</span>
                </RouterLink>
                <div className="px-3 py-2 flex flex-col">
                  <span className="font-semibold text-fg mb-1 truncate">{user?.firstName} {user?.lastName}</span>
                  <div className="flex flex-nowrap items-center gap-1 whitespace-nowrap overflow-x-auto">
                    {user?.role === 'fondateur' ? (
                      <>
                        <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-red-500/20 text-red-400">
                          Fondateur
                        </span>
                        <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-orange-500/20 text-orange-400">
                          Admin
                        </span>
                      </>
                    ) : user?.role === 'freelancer_admin' ? (
                      <>
                        <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-yellow-500/20 text-yellow-500">
                          Freelancer
                        </span>
                        <span className="inline-flex px-2 py-0.5 rounded-md text-xs font-medium bg-orange-500/20 text-orange-400">
                          Admin
                        </span>
                      </>
                    ) : (
                      <span className={`inline-flex px-2 py-0.5 rounded-md text-xs font-medium ${
                        user?.role === 'admin' ? 'bg-orange-500/20 text-orange-400' :
                        user?.role === 'freelancer' ? 'bg-yellow-500/20 text-yellow-500' :
                        'bg-accent-strong/20 text-accent-soft'
                      }`}>
                        {user?.role === 'admin' ? 'Admin' :
                         user?.role === 'freelancer' ? 'Freelancer' :
                         user?.clientType || 'Utilisateur'}
                      </span>
                    )}
                    {/* Tag d'abonnement pour mobile - visible uniquement pour les utilisateurs standards */}
                    {(!user?.role || 
                      (user?.role !== 'admin' && 
                       user?.role !== 'fondateur' && 
                       user?.role !== 'freelancer' &&
                       user?.role !== 'freelancer_admin')) && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-accent/10 text-accent-soft">
                          <Crown className="w-3 h-3 mr-1" />
                          {getSubscriptionName()}
                        </span>
                      )}
                  </div>
                </div>
              </>
            )}
            {isAuthenticated ? (
              <button
                className="btn-primary w-full !justify-start !px-4 !gap-0"
                aria-label="Déconnecter"
                onClick={logout}
              >
                <LogOut className="h-5 w-5 mr-2" />
                <span>Déconnexion</span>
              </button>
            ) : (
              <div className="mt-3 border-t border-line pt-2">
                <button
                  type="button"
                  className="flex min-h-11 w-full items-center justify-center gap-1.5 rounded-lg text-sm text-fg-subtle transition-colors hover:text-fg"
                  aria-label="Se connecter"
                  onClick={() => {
                    setIsOpen(false);
                    setIsLoginModalOpen(true);
                  }}
                >
                  <User className="h-4 w-4" aria-hidden="true" />
                  <span>Connexion</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Login Modal */}
      <Modal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        title="Connexion"
      >
        <LoginForm onSuccess={handleLoginSuccess} onRegisterClick={openRegisterModal} />
      </Modal>
      
      {/* Register Modal */}
      <RegisterModal 
        isOpen={isRegisterModalOpen} 
        onClose={() => setIsRegisterModalOpen(false)} 
        onSwitchToLogin={openLoginModal}
      />
    </>
  );
};

export default Navbar;